import EditableInputView from "./editable_input_view";

import ExceptionHandling from "shared/logging/exception_handling";

import "vendor/backbone/stickit";
import "select2";

import _ from "underscore";
import $ from "jquery";

const SEPARATOR_ITEM_TYPE = "separator";
const SEPARATOR_ID = "__separator__";
const DEFAULT_SELECT_PAGE_SIZE = 50;

class EditableSelectInputView extends EditableInputView {
  get tagName() { return 'select'; }

  beforeRender() {
    // select2 needs to be wrapped in a div so that it can append its own ui elements somewhere.
    // here we will create the select with all attributes specified by the user and then
    // wrap it in a div that becomes the view's element
    this.options.disabled && this.$el.attr('disabled', 'disabled');
    this.$el.addClass("select2").addClass("select2-initial");
    const oldEl = this.$el;
    const wrapperClass = this.options.wrapperClass || "";
    this.setElement($("<div>").addClass(wrapperClass));
    this.$el.append(oldEl);

    this._select2Options = this._generateSelect2Options();

    // support providing an initial selection on remote sourced contents
    if (this.options.href && this.model.has(this.attr)) {
      const initialId = this.model.get(this.attr);
      const initialText = this.model.get(`${this.attr}_label`) || "1 selected";

      // Create the DOM option that is pre-selected by default
      const defaultOption = new Option(initialText, initialId, true, true);

      // Append it to the select
      this.$(".select2-initial").append(defaultOption);
    }

    if (this.options.name) {
      this._setSelectName(this.options.name);
    }

    if (this.options.href && this.options.placeholder) {
      this._addEmptyOptionForPlaceholder();
    }

    if (this._select2Options.tags) {
      this._enableTagsSupport();
    }

    if (this._select2Options.allowClear) {
      this._hideMenuOnClear();
    }
  }

  lock(value) {
    this.$(".select2-initial").prop("disabled", value);
  }

  _generateSelect2Options() {
    const opts = _.clone(this.options.select2Options || {});
    if (this.options.search === false) {
      // search is explicitly disabled
      opts.minimumResultsForSearch = -1;
    }
    if (this.options.placeholder) {
      opts.placeholder = this.options.placeholder;
    }

    if (this.options.href) {
      const textAttr = this.options.textAttr || 'text';
      const itemsAttr = this.options.itemsAttr || 'items';
      const pageSize = this.options.pageSize || DEFAULT_SELECT_PAGE_SIZE;
      const requiredAttribute = this.options.requiredAttribute;
      opts.ajax = {
        url: this.options.href,
        delay: 250,
        headers: {
          "X-Select2": "1"
        },
        dataType: 'json',
        data(params) {
          const page = params.page || 1;

          const data = {
            query: params.term, // search term
            start: (page - 1) * pageSize,
            limit: pageSize,
            ajax: true
          };
          return data;
        },
        processResults(data, params) {
          const page = params.page || 1;
          const more = (page * pageSize) < data.total_results; // whether or not there are more results available

          // parse the results into the format expected by Select2.
          if (data[itemsAttr]) {
            const results = {
              pagination: {
                more
              },
              results: data[itemsAttr].map(row => {
                let resultRow = row;
                if (requiredAttribute) {
                  resultRow = _.extend({}, resultRow, { disabled: _.isEmpty(row[requiredAttribute]) });
                }

                if (textAttr === 'text') {
                  return resultRow;
                } else {
                  return _.extend({}, resultRow, { text: resultRow[textAttr] });
                }
              })
            };

            return results;
          } else {
            ExceptionHandling.logWarning(`Select2 Controls.js: Response data did not contain outer key of '${itemsAttr}'`);

            return {
              results: []
            };
          }
        }
      };

      if (typeof this.options.allowClear !== "undefined") {
        opts.allowClear = this.options.allowClear;
      } else if (this.options.placeholder) {
        opts.allowClear = true;
      }

      // allowClear requires an empty option at the top of the select
      // otherwise clearing out the selection results in a val of null
      //  (which won't be sent to server if using a form serialize and jQuery ajax post)
      if (!this.options.placeholder && opts.allowClear) {
        ExceptionHandling.logWarning('Controls.EditableSelectInputView: allowClear can only be true if a placeholder exists.');
        opts.allowClear = false;
      }
    } else {
      opts.allowClear = this.options.allowClear;
      if (opts.data) {
        opts.data = this._filteredDataWithSelection(opts.data, opts.placeholder);
      }
    }

    opts.width = this.options.width;
    opts.theme = 'bootstrap';
    return opts;
  }

  _setSelectName(name) {
    this.$(".select2-initial").attr("name", name);
  }

  _addEmptyOptionForPlaceholder() {
    const emptyOption = new Option('', '');
    this.$(".select2-initial").append(emptyOption);
  }

  _enableTagsSupport() {
    this.$(".select2-initial").attr("multiple", "multiple");

    const initialValues = this.model.get(this.attr);

    // add item(s) to the list - this is required to have the initial tags present in the control
    if (initialValues) {
      if (_.isArray(initialValues)) {
        initialValues.forEach(param => {
          if (param) {
            const option = new Option(param, param);
            this.$(".select2-initial").append(option);
          }
        });
      } else {
        ExceptionHandling.logWarning(`Controls.EditableSelectInputView: model attribute should be an array of strings when using 'tags' option, got '${initialValues}' instead.`);
      }
    }

    // preventing tag dropdown from opening when closing a tag - https://github.com/select2/select2/issues/3209#issuecomment-149663474
    // this is particularly necessary because we have 'selectOnClose' enabled so that a user can type an option then click or tab away
    // without hitting enter - but that also meant an open menu will autoselect the first item in list, so don't open menu when removing tags!
    this.$(".select2-initial").on("select2:unselect", evt => {
      if (!evt.params.originalEvent) {
        return;
      }

      evt.params.originalEvent.stopPropagation();
    });
  }

  // Go back to select2 3.5 behavior where menu does not re-open when hitting clear
  // https://github.com/select2/select2/issues/3320#issuecomment-291739602
  _hideMenuOnClear() {
    this.$(".select2-initial").on('select2:unselecting', function() {
      const opts = $(this).data('select2').options;
      opts.set('disabled', true);
      setTimeout(() => {
        opts.set('disabled', false);
      }, 1);
    });
  }

  _filteredDataWithSelection(data, placeholder) {
    const newData = _.compact(_.clone(data).map(row => {
      // handle grouped data
      if (row.children) {
        return _.extend({}, row, { children: row.children.map(row => this._filteredRowWithSelection(row)) });
      } else {
        return this._filteredRowWithSelection(row);
      }
    }));

    if (placeholder && (!newData[0] || newData[0].id !== '')) {
      newData.unshift({ id: '', text: '' });
    }

    return newData;
  }

  _filteredRowWithSelection(row) {
    if (typeof (row.presence) === "function" && !row.presence.call(this, this.model)) {
      return null;
    } else if (row.id && row.id === this.model.get(this.attr)) {
      return _.extend({}, row, { selected: true });
    } else {
      return row;
    }
  }

  _buildDeprecatedSelectCollection() {
    if (this.options.items) {
      ExceptionHandling.logWarning('Controls.EditableSelectInputView [DEPRECATION WARNING]: options.items is deprecated, use options.select2Options.data instead.');
    }

    const items = _.clone(this.options.items) || [];
    if (this.options.placeholder) {
      items.unshift({ value: "", label: "" });
    }

    const self = this;
    return _.compact(_.map(items, function(item) {
      // Build the full list of options unless an option defines "presence," in which if
      // that method returns false then the option will be removed from the list
      if (typeof (item.presence) === "function" && !item.presence.call(this, self.model)) {
        return null;
      }

      if (item.type === SEPARATOR_ITEM_TYPE) {
        return _.extend(item, { value: SEPARATOR_ID, label: "", disabled: "disabled" });
      } else {
        return item;
      }
    }));
  }

  _useDeprecatedSelectedCollection() {
    return this.options.items && !(this.options.select2Options && this.options.select2Options.data);
  }

  _initBindings() {
    const stickitOptions = {
      observe: this.attr,
      getVal($el, event, options) {
        // Side effect - capture the selected item's text and put that in the model
        // This is useful for controls that listen for change events (for example to update history/URL)
        // If a value is not found, likely it is a jasmine test that is setting a value that is not in the list
        let selectedText = '';
        let selection = {};
        const attrLabel = `${this.attr}_label`;
        if ($el.data('select2')) {
          // For now we are only supporting single selections, this code would need to change to support multi select
          selection = _.extend({}, $el.select2("data")[0]);

          if (!_.isEmpty(selection)) {
            selectedText = selection.text;
          }
        }

        if (this.options.extras) {
          selection[attrLabel] = selectedText;
          this.model.set(selection, { silent: true });
        } else {
          this.model.set(attrLabel, selectedText, { silent: true });
        }

        // value for stickit to update model attribute
        return $el.val();
      },

      initialize($el, model, stickitOptions) {
        $el.select2(this._select2Options);
      }
    };

    // stickit gets really angry if you pass selectOptions: null (or an empty collection); the binding from model back to UI is then broken
    //  so we only want to add this option if we are going to build the collection via the deprecated path
    if (this._useDeprecatedSelectedCollection()) {
      stickitOptions.selectOptions = {
        collection: this._buildDeprecatedSelectCollection
      };
    }

    this.stickit(this.model, {
      ".select2": stickitOptions
    });
  }
}

export default EditableSelectInputView;
